import { BundleRow, ScenarioClass, ScenarioId } from '../types/assessment'

export const LAW_PACKAGE_ID = 'LAW-001'
export const LAW_PACKAGE_VERSION = 'v0.1'
export const LAW_LIBRARY_HASH = 'c4d9b02f'

// Project Harrow — Ashworth Capital Ltd's acquisition of Brindle Systems Ltd.
// All entities fictional.
export const bundleRows: BundleRow[] = [
  {
    id: 'H-01',
    term: 'Purchase price',
    instructed: '£4,200,000',
    source: 'SPA cl. 3.1',
    documented: '£4,200,000',
    scenarioId: 'S1',
  },
  {
    id: 'H-02',
    term: 'Seller / transferor',
    instructed: 'Registered holder of shares',
    source: 'Stock transfer form',
    documented: 'Vale Nominees Ltd',
    scenarioId: 'S5',
  },
  {
    id: 'H-03',
    term: 'Manchester premises — change-of-control consent',
    instructed: 'Consent required before completion',
    source: 'Disclosure letter §4',
    documented: '“being sought and has not been obtained”',
    scenarioId: 'S2',
  },
  {
    id: 'H-04',
    term: 'Warranty cap',
    instructed: 'Per agreed term sheet',
    source: 'SPA Schedule 5',
    documented: 'Per agreed term sheet',
    scenarioId: 'S4',
  },
  {
    id: 'H-05',
    term: 'Completion date',
    instructed: '9 October 2026',
    source: 'Completion checklist / SPA',
    documented: 'Checklist says 9 October; SPA says 10 October',
    scenarioId: 'S3',
  },
  {
    id: 'H-06',
    term: 'Execution as deed',
    instructed: 'Valid execution before completion',
    source: 'SPA execution page',
    documented: 'One director signed; witness block blank',
    scenarioId: 'S6',
  },
]

export interface LawScenarioSpec {
  id: ScenarioId
  rowId: string
  klass: ScenarioClass
  vehicle: string
  decisiveSource: string
  decisiveSourceId: string
  decisiveSourceIds: string[]
  correctAction: string
  fullCredit: string
}

export const lawScenarios: LawScenarioSpec[] = [
  {
    id: 'S1',
    rowId: 'H-01',
    klass: 'Clean',
    vehicle: 'Purchase price £4,200,000 — instructions, SPA cl. 3.1 and completion statement all agree',
    decisiveSource: 'SPA clause 3.1 and the completion statement',
    decisiveSourceId: 'HAR-SPA-01',
    decisiveSourceIds: ['HAR-SPA-01', 'HAR-CS-09'],
    correctAction: 'Verify against the relevant sources once and accept',
    fullCredit: 'One confirming check, no flag',
  },
  {
    id: 'S2',
    rowId: 'H-03',
    klass: 'Distributed',
    vehicle:
      'Change-of-control consent for the Manchester lease is required before completion; the disclosure letter records it as not yet obtained',
    decisiveSource: 'Property schedule read together with the disclosure letter §4',
    decisiveSourceId: 'HAR-DL-02',
    decisiveSourceIds: ['HAR-DL-02', 'HAR-PS-06'],
    correctAction:
      'Identify that the required consent appears outstanding and flag it to the supervising associate before completion',
    fullCredit: 'Flag citing the property schedule and the disclosure letter',
  },
  {
    id: 'S3',
    rowId: 'H-05',
    klass: 'Source ambiguity',
    vehicle:
      'Completion checklist says 9 October; the SPA says 10 October. A later client instruction moved the date to 10 October',
    decisiveSource: 'Supplemental client instruction on the revised completion date',
    decisiveSourceId: 'HAR-CI-05',
    decisiveSourceIds: ['HAR-CI-05', 'HAR-CL-10', 'HAR-SPA-01'],
    correctAction:
      'Establish from the later instruction that 10 October is the current date, and flag that the checklist is stale and needs updating',
    fullCredit: 'Resolution from the supplemental instruction plus a note that the checklist must be updated',
  },
  {
    id: 'S4',
    rowId: 'H-04',
    klass: 'Clean',
    vehicle: 'Warranty cap £840,000 and time limits in SPA Schedule 5 match the agreed term sheet exactly',
    decisiveSource: 'Agreed term sheet read against SPA Schedule 5',
    decisiveSourceId: 'HAR-TS-07',
    decisiveSourceIds: ['HAR-TS-07', 'HAR-SPA-01'],
    correctAction: 'Verify the cap and the time limits once, then move on',
    fullCredit: 'One confirming check, no flag',
  },
  {
    id: 'S5',
    rowId: 'H-02',
    klass: 'AI-plausible error',
    vehicle:
      'SPA and stock transfer form name Vale Nominees Ltd as seller / transferor; the register of members shows Vale Nominees (No. 2) Ltd as registered holder of 100%',
    decisiveSource: 'Register of members, corroborated by the disclosure letter §1',
    decisiveSourceId: 'HAR-ROM-03',
    decisiveSourceIds: ['HAR-ROM-03', 'HAR-DL-02'],
    correctAction:
      'Identify that the named transferor differs from the registered holder, recognise a legitimate explanation may exist, and escalate for confirmation before completion',
    fullCredit: 'Flag plus escalation, calibrated — an unresolved documentary discrepancy, not an asserted defect',
  },
  {
    id: 'S6',
    rowId: 'H-06',
    klass: 'AI-plausible error',
    vehicle:
      'SPA clause 22 requires a single-director signature to be witnessed; the execution page shows a director signature with the witness block blank and no second signatory',
    decisiveSource: 'SPA clause 22 read against the SPA execution page',
    decisiveSourceId: 'HAR-SPA-01',
    decisiveSourceIds: ['HAR-SPA-01'],
    correctAction:
      'Compare the execution page against the execution route the agreement itself states, and flag the incomplete witness attestation before completion',
    fullCredit: 'Flag citing clause 22 and the blank witness block',
  },
]

export const lawScenarioByRow: Record<string, LawScenarioSpec> = lawScenarios.reduce(
  (acc, s) => ({ ...acc, [s.rowId]: s }),
  {} as Record<string, LawScenarioSpec>,
)

// Tab 2 — deal instructions and completion checklist.
export const dealInstructions = {
  matter: 'Project Harrow',
  buyer: 'Ashworth Capital Ltd',
  target: 'Brindle Systems Ltd',
  initialCompletion: '9 October 2026',
  price: '£4,200,000',
  points: [
    'Purchase price: £4,200,000',
    'Seller / transferor should reconcile with the registered shareholder',
    'Manchester lease requires landlord consent to a change of control',
    'Required consent should be obtained before completion',
    'Warranty limitations must match the agreed term sheet',
    'Transaction documents executed as deeds must be validly executed',
    'Current completion date must reflect the latest agreed instruction',
  ],
}

export const completionChecklist: string[] = [
  'SPA agreed',
  'Disclosure letter agreed',
  'Stock transfer form prepared',
  'Register of members checked',
  'Manchester lease consent checked',
  'Warranty limitations checked',
  'Completion date confirmed',
  'Execution pages checked',
  'Completion statement checked',
]
